import type { PromotionBadgeVariant } from '@/components/Promotions/PromotionBadge'
import type { PromotionCardProps } from '@/components/Promotions/PromotionCard'
import { getCMSReferenceHref, type Locale, type TemplatePathMapLike } from '@/lib/i18n/config'
import type { Media } from '@/payload-types'

import {
  getPromotionExpireEndOfDayMs,
  type PromotionListItem,
  type PromotionLocationFilter,
} from './promotionsQuery'
import { getPromotionLocationBadgeLabels } from './locationDisplay'

export const DEFAULT_PROMOTION_HERO_IMAGE =
  '/media/landing/best-deal-promotions/promo-refer-a-friend.png'

export type PromotionCardLabels = {
  imageAltFallback: string
  offerEndsInDaysHours: string
  offerEndsInHoursMinutes: string
  offerEndsInMonthsDays: string
  offerEndsInYearsMonths: string
  offerEndsSoon: string
  permanentOffer: string
  readMore: string
  readMoreAriaLabel: string
}

export type GetPromotionCardPropsOptions = {
  layout?: 'horizontal' | 'vertical'
  locationLabels?: Record<PromotionLocationFilter, string>
  /**
   * `null` keeps the badge on the stable "ends soon" label so SSR and the first
   * client render match. A number is the clock used for the live remaining time.
   */
  nowMs?: number | null
  showLocationBadge?: boolean
  templatePaths?: TemplatePathMapLike | null
}

const MINUTES_PER_DAY = 60 * 24
const DAYS_PER_MONTH = 30
const MONTHS_PER_YEAR = 12

export function formatOfferTimeRemaining(
  expireDate: string,
  templates: {
    daysHours: string
    hoursMinutes: string
    monthsDays: string
    yearsMonths: string
  },
  nowMs = Date.now(),
): string {
  const expiresAt = getPromotionExpireEndOfDayMs(expireDate)
  const remainingMs = Math.max(0, expiresAt - nowMs)

  const totalMinutes = Math.floor(remainingMs / (1000 * 60))
  const totalDays = Math.floor(totalMinutes / MINUTES_PER_DAY)
  const totalMonths = Math.floor(totalDays / DAYS_PER_MONTH)

  if (totalMonths > MONTHS_PER_YEAR) {
    const years = Math.floor(totalMonths / MONTHS_PER_YEAR)
    const months = totalMonths % MONTHS_PER_YEAR

    return templates.yearsMonths
      .replace('{years}', String(years))
      .replace('{months}', String(months))
  }

  if (totalDays > DAYS_PER_MONTH) {
    const months = totalMonths
    const days = totalDays % DAYS_PER_MONTH

    return templates.monthsDays
      .replace('{months}', String(months))
      .replace('{days}', String(days))
  }

  if (totalMinutes >= MINUTES_PER_DAY) {
    const days = totalDays
    const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / 60)

    return templates.daysHours
      .replace('{days}', String(days))
      .replace('{hours}', String(hours))
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return templates.hoursMinutes
    .replace('{hours}', String(hours))
    .replace('{minutes}', String(minutes))
}

export function getPromotionCardImage(
  promotion: PromotionListItem,
  imageAltFallback: string,
): {
  alt: string
  fallbackSrc: string
  resource?: Media | null
} {
  const heroImage = promotion.heroImage

  if (heroImage && typeof heroImage === 'object') {
    return {
      alt: heroImage.alt || imageAltFallback.replace('{title}', promotion.title),
      fallbackSrc: DEFAULT_PROMOTION_HERO_IMAGE,
      resource: heroImage,
    }
  }

  return {
    alt: imageAltFallback.replace('{title}', promotion.title),
    fallbackSrc: DEFAULT_PROMOTION_HERO_IMAGE,
    resource: undefined,
  }
}

export type PromotionOfferDetailsData = {
  bookingWindow?: string
  minimumStay?: string
  moveIn?: string
}

function optionalPromotionText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed || undefined
}

export function getPromotionOfferDetails(
  promotion: Pick<PromotionListItem, 'bookingDay' | 'minimumStay' | 'movingDay'>,
): PromotionOfferDetailsData | undefined {
  const bookingWindow = optionalPromotionText(promotion.bookingDay)
  const moveIn = optionalPromotionText(promotion.movingDay)
  const minimumStay = optionalPromotionText(promotion.minimumStay)

  if (!bookingWindow && !moveIn && !minimumStay) {
    return undefined
  }

  return {
    ...(bookingWindow ? { bookingWindow } : {}),
    ...(moveIn ? { moveIn } : {}),
    ...(minimumStay ? { minimumStay } : {}),
  }
}

export function hasPromotionOfferDetails(
  promotion: Pick<PromotionListItem, 'bookingDay' | 'minimumStay' | 'movingDay'>,
): boolean {
  return getPromotionOfferDetails(promotion) !== undefined
}

export function shouldShowPromotionCountdown(
  promotion: Pick<PromotionListItem, 'expireDate' | 'isPermanent' | 'showCountdown'>,
): boolean {
  if (promotion.isPermanent || promotion.showCountdown === false) {
    return false
  }

  return Boolean(promotion.expireDate)
}

export function getPromotionStatusBadge(
  promotion: Pick<PromotionListItem, 'expireDate' | 'isPermanent' | 'showCountdown'>,
  labels: Pick<
    PromotionCardLabels,
    | 'offerEndsInDaysHours'
    | 'offerEndsInHoursMinutes'
    | 'offerEndsInMonthsDays'
    | 'offerEndsInYearsMonths'
    | 'offerEndsSoon'
    | 'permanentOffer'
  >,
  nowMs?: number | null,
): { label: string; variant: PromotionBadgeVariant } | null {
  if (promotion.isPermanent) {
    return { label: labels.permanentOffer, variant: 'permanent' }
  }

  if (!promotion.expireDate) {
    return null
  }

  if (promotion.showCountdown === false || nowMs === null) {
    return { label: labels.offerEndsSoon, variant: 'timed' }
  }

  return {
    label: formatOfferTimeRemaining(
      promotion.expireDate,
      {
        daysHours: labels.offerEndsInDaysHours,
        hoursMinutes: labels.offerEndsInHoursMinutes,
        monthsDays: labels.offerEndsInMonthsDays,
        yearsMonths: labels.offerEndsInYearsMonths,
      },
      nowMs,
    ),
    variant: 'timed',
  }
}

export type PromotionCountdownMode =
  | 'daysHoursMinutes'
  | 'hoursMinutesSeconds'
  | 'yearsMonthsDays'

export type PromotionCountdownParts = {
  expired: boolean
  mode: PromotionCountdownMode
  units: [number, number, number]
}

export function getPromotionCountdownParts(expireDate: string): PromotionCountdownParts {
  const remainingMs = Math.max(0, getPromotionExpireEndOfDayMs(expireDate) - Date.now())
  const totalSeconds = Math.floor(remainingMs / 1000)
  const totalMinutes = Math.floor(totalSeconds / 60)
  const totalDays = Math.floor(totalMinutes / MINUTES_PER_DAY)
  const totalMonths = Math.floor(totalDays / DAYS_PER_MONTH)

  if (totalMonths > MONTHS_PER_YEAR) {
    return {
      expired: remainingMs === 0,
      mode: 'yearsMonthsDays',
      units: [
        Math.floor(totalMonths / MONTHS_PER_YEAR),
        totalMonths % MONTHS_PER_YEAR,
        totalDays % DAYS_PER_MONTH,
      ],
    }
  }

  if (totalMinutes >= MINUTES_PER_DAY) {
    return {
      expired: remainingMs === 0,
      mode: 'daysHoursMinutes',
      units: [
        totalDays,
        Math.floor((totalSeconds % 86400) / 3600),
        Math.floor((totalSeconds % 3600) / 60),
      ],
    }
  }

  return {
    expired: remainingMs === 0,
    mode: 'hoursMinutesSeconds',
    units: [
      Math.floor(totalSeconds / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60,
    ],
  }
}

export function getPromotionCardProps(
  promotion: PromotionListItem,
  locale: Locale,
  labels: PromotionCardLabels,
  options: GetPromotionCardPropsOptions = {},
): PromotionCardProps {
  const {
    layout = 'vertical',
    locationLabels: locationLabelsMap,
    nowMs,
    showLocationBadge = true,
    templatePaths,
  } = options

  const image = getPromotionCardImage(
    promotion,
    labels.imageAltFallback.replace('{title}', promotion.title),
  )

  const statusBadge = getPromotionStatusBadge(promotion, labels, nowMs)
  const badges = statusBadge ? [statusBadge] : []

  const locationLabels =
    showLocationBadge && locationLabelsMap
      ? getPromotionLocationBadgeLabels(promotion.locations, locationLabelsMap)
      : undefined

  return {
    badges,
    body: promotion.shortDescription ?? '',
    cta: {
      ariaLabel: labels.readMoreAriaLabel.replace('{title}', promotion.title),
      href: getCMSReferenceHref('promotions', promotion.slug, locale, templatePaths),
      label: labels.readMore,
    },
    image: promotion.heroImage,
    imageAlt: image.alt,
    layout,
    locationLabels,
    offerDetails: getPromotionOfferDetails(promotion),
    title: promotion.title,
  }
}
